import React, { useRef } from 'react';
import { Box, IconButton, FormHelperText } from '@mui/material';
import { AgronodButton, AgronodTypography, Tooltip } from '@agronod/mui-components';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';

/** Keep in sync with the backend IsLogoUrlOrDataUri validator. */
const MAX_LOGO_BYTES = 100 * 1024;
const ALLOWED_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
/** Below this, logos render visibly pixelated once scaled up to the 40–56px display sizes used across the app. */
const MIN_RASTER_DIMENSION = 128;

interface IssuerLogoFieldProps {
  /** Current logo — a data URI or remote URL, or empty when unset. */
  value?: string | null;
  /** Called with a data URI on upload, or null when cleared. */
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

/**
 * Admin logo picker for an issuer. The chosen file is read as a base64 data URI
 * and handed to the parent, which stores it directly in the issuer's logoUrl
 * field — no separate upload endpoint or blob storage. Size and type are
 * validated client-side to match the backend constraint.
 */
const IssuerLogoField: React.FC<IssuerLogoFieldProps> = ({ value, onChange, disabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      enqueueSnackbar('Otillåtet filformat. Använd SVG, PNG, JPEG eller WebP.', {
        variant: 'error',
      });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      enqueueSnackbar(`Filen är för stor (max ${MAX_LOGO_BYTES / 1024} kB).`, {
        variant: 'error',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = reader.result;
      if (typeof dataUri !== 'string') return;

      // SVGs are resolution-independent, so only raster formats need a
      // minimum pixel size check to avoid a pixelated logo once scaled up.
      if (file.type === 'image/svg+xml') {
        onChange(dataUri);
        return;
      }

      const image = new Image();
      image.onload = () => {
        if (image.naturalWidth < MIN_RASTER_DIMENSION || image.naturalHeight < MIN_RASTER_DIMENSION) {
          enqueueSnackbar(
            `Bilden är för lågupplöst (minst ${MIN_RASTER_DIMENSION}×${MIN_RASTER_DIMENSION} px krävs).`,
            { variant: 'error' },
          );
          return;
        }
        onChange(dataUri);
      };
      image.onerror = () => {
        enqueueSnackbar('Kunde inte läsa filen.', { variant: 'error' });
      };
      image.src = dataUri;
    };
    reader.onerror = () => {
      enqueueSnackbar('Kunde inte läsa filen.', { variant: 'error' });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <AgronodTypography variant="body2" gutterBottom sx={{
        color: "text.secondary"
      }}>
        Logga
      </AgronodTypography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {value ? (
            <Box
              component="img"
              src={value}
              alt="Logga"
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <AgronodTypography variant="caption" sx={{
              color: "text.disabled"
            }}>
              Ingen
            </AgronodTypography>
          )}
        </Box>

        <AgronodButton
          variant="outlined"
          size="small"
          startIcon={<UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled ?? false}
        >
          {value ? 'Byt logga' : 'Ladda upp logga'}
        </AgronodButton>

        {value && (
          <Tooltip title="Ta bort logga">
            <IconButton
              size="small"
              color="error"
              aria-label="Ta bort logga"
              onClick={() => onChange(null)}
              disabled={disabled ?? false}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <input
          ref={inputRef}
          type="file"
          hidden
          accept="image/svg+xml,image/png,image/jpeg,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </Box>
      <FormHelperText>
        SVG, PNG, JPEG eller WebP, max {MAX_LOGO_BYTES / 1024} kB. Rastergrafik
        kräver minst {MIN_RASTER_DIMENSION}×{MIN_RASTER_DIMENSION} px.
      </FormHelperText>
    </Box>
  );
};

export default IssuerLogoField;
